import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config/ApiConfig';
import { formatString } from './utils/StringUtils';
import ExcelUploadModal from './components/modalServices/ExcelUploadModal';
import DefaultModal from './components/modals/DefaultModal';
import useDefaultModal from './components/hooks/useDefaultModal';
import ConfirmModal from './components/modals/ConfirmModal';
import useConfirmModal from './components/hooks/useConfirmModal';
import usePagination10 from './components/hooks/usePagination10';
import { formatCurrency, unformatCurrency, formatQuantidade } from './utils/formatMoeda';
import Pagination10 from './components/Pagination10';
import PageFooter from './components/PageFooter';
import Layout from './components/Layout';
import ResponsiveShadow from './components/ResponsiveShadow';
import PageHeader from './components/PageHeader';
import PageContent from './components/PageContent';
import Toolbar from './components/Toolbar';
import TableContainer from './components/TableContainer';
import TableHeader from './components/TableHeader';
import DfdFindItemModal from './components/modalServices/DfdFindItemModal';

function AprovaDfdItens() {
  const [dfd, setDfd] = useState({
    cnpj: '',
    num: '',
    dataContratacao: '',
    requisitante: '',
    objeto: '',
    prioridade: '',
    justificativaPrioridade: '',
    justificativaDemanda: '',
    tipo: '',
    usuario: '',
  });
  const [dfdItens, setDfdItens] = useState([]);
  const [showExcelUploadModal, setShowExcelUploadModal] = useState(false);
  const [showFindModal, setShowFindModal] = useState(false);
  const [unidades, setUnidades] = useState([]);
  const [categoriaItem, setCategoriaItem] = useState([]);
  const [shouldFetch, setShouldFetch] = useState(false);
  const token = localStorage.getItem('token');
  const location = useLocation();
  const { setDefaultModal, showDefaultModal } = useDefaultModal();
  const { setConfirmModal, showConfirmModal } = useConfirmModal();
  const totalItems = dfdItens.length;
  const { setPagination } = usePagination10(totalItems);
  const { startIndex, rowsPerPage } = setPagination;
  const selectedDfdItens = dfdItens.slice(startIndex, startIndex + rowsPerPage);
  useEffect(() => {
    if (location.state && location.state.dfd) {
      setDfd(location.state.dfd);
    }
    if (dfd.cnpj !== '') {
      axios
        .get(`${API_BASE_URL.DFD_ITEM}/demanda`, {
          params: {
            cnpj: dfd.cnpj,
            num: dfd.num,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        .then(result => {
          const formattedData = result.data.map(item => ({
            ...item,
            vlrUnit: formatCurrency(item.vlrUnit),
          }));
          setDfdItens(formattedData);
        })
        .catch(error => {
          console.error('Erro ao obter listas:', error);
        });

      axios
        .get(`${API_BASE_URL.UNIDADE_MEDIDA}/all`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        .then(result => {
          setUnidades(result.data);
        })
        .catch(error => {
          console.error('Erro ao obter unidades:', error);
        });

      axios
        .get(`${API_BASE_URL.CATEGORIA_ITEM}/all`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        .then(result => {
          setCategoriaItem(result.data);
        })
        .catch(error => {
          console.error('Erro ao obter unidades:', error);
        });
    }
  }, [dfd, shouldFetch]);

  if (!dfdItens) {
    return <div>Loading...</div>;
  }

  function handleDelete(dfdToDelete) {
    showConfirmModal('delete', () => handleDeleteConfirm(dfdToDelete));
  }

  function handleDeleteConfirm(itemToDelete) {
    axios
      .delete(`${API_BASE_URL.DFD_ITEM}/delete`, {
        params: {
          cnpj: itemToDelete.cnpj,
          dfdNum: itemToDelete.dfdNum,
          id: itemToDelete.id,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(result => {
        setShouldFetch(!shouldFetch);
        showDefaultModal('success', 'O registro foi excluído com sucesso!');
      })
      .catch(error => {
        console.error('Erro ao excluir o item:', error);
        showDefaultModal('error', 'Erro ao excluir o registro!');
      });
  }

  function calculateTotal(quantidade, vlrUnit) {
    const quantidadeNum = parseFloat(quantidade) || 0;
    const vlrUnitNum = unformatCurrency(vlrUnit) || 0;
    return formatCurrency(quantidadeNum * vlrUnitNum);
  }

  const handleInputChange = (localIndex, field, value) => {
    const globalIndex = startIndex + localIndex;
    // Verifica se o índice global existe antes de tentar atualizar
    if (!dfdItens[globalIndex]) {
      console.error('Tentando acessar um índice inexistente:', globalIndex);
      return;
    }
    const updatedDfdItens = [...dfdItens];
    if (field === 'vlrUnit') {
      updatedDfdItens[globalIndex][field] = formatCurrency(
        unformatCurrency(value)
      );
    } else {
      updatedDfdItens[globalIndex][field] = value;
    }

    setDfdItens(updatedDfdItens);
  };

  const handleSaveAll = () => {
    const itemsToSave = dfdItens.map(item => ({
      ...item,
      vlrUnit: unformatCurrency(item.vlrUnit),
    }));
    axios
      .post(`${API_BASE_URL.DFD_ITEM}/saveAll`, itemsToSave, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(result => {
        showDefaultModal('success', 'Itens atualizados com sucesso!');
      })
      .catch(error => {
        console.error('Erro ao atualizar os itens:', error);
        showDefaultModal('error', 'Erro ao salvar o registro');
      });
  };

  async function handleDownload() {
    try {
      const response = await axios.post(
        `${API_BASE_URL.DFD}/exportExcel`,
        dfd,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          responseType: 'blob', // Para lidar com o arquivo como binário
        }
      );

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `DFD ${formatString(dfd.num, 4)}.xlsx`;
      link.click();
    } catch (error) {
      console.error('Erro ao baixar o arquivo:', error);
    }
  }

  return (
    <Layout>
      <ResponsiveShadow>
        <PageHeader title="Itens do DFD" icon="bi-boxes" backButton={false} closeRoute="/aprova_dfd" closeState={{ dfd: dfd }} />
        <PageContent>
          <Toolbar
            actions={[
              { key: 'add', label: 'Adicionar Item', icon: 'bi-plus-lg', variant: 'success', show: dfd.tramitacao === 'EM ELABORAÇÃO', onClick: () => setShowFindModal(true) },
              { key: 'save', label: 'Salvar Todos', icon: 'bi-save', variant: 'primary', show: dfd.tramitacao === 'EM ELABORAÇÃO', onClick: handleSaveAll },
              { key: 'export', label: 'Exportar Excel', icon: 'bi-file-excel', variant: 'secondary', onClick: handleDownload },
              { key: 'import', label: 'Importar Excel', icon: 'bi-upload', variant: 'warning', show: dfd.tramitacao === 'EM ELABORAÇÃO', onClick: () => setShowExcelUploadModal(true) },
            ]}
          />
      <TableContainer tableClass="table-custom-radius">
        <TableHeader columns={[
          { label: 'Grupo', width: '5%', align: 'center' },
          { label: 'Classe', width: '5%', align: 'center' },
          { label: 'Código', width: '5%', align: 'center' },
          { label: 'Descrição', align: 'center' },
          { label: 'Categoria', width: '10%', align: 'center' },
          { label: 'Unidade', width: '10%', align: 'center' },
          { label: 'Quantidade', width: '10%', align: 'center' },
          { label: 'Valor Unitário', width: '10%', align: 'center' },
          { label: 'Valor Total', width: '10%', align: 'center' },
          ...(dfd.tramitacao === 'EM ELABORAÇÃO' ? [{ label: 'Ação', align: 'center' }] : []),
        ]} />
        <tbody>
          {selectedDfdItens.length > 0 ? (
            selectedDfdItens.map((c, index) => (
              <tr key={`${c.cnpj}-${c.dfdNum}-${c.id}`}>
                <td style={{ width: '5%' }}>{c.grupo}</td>
                <td style={{ width: '5%' }}>{c.classe}</td>
                <td style={{ width: '5%' }}>{c.item}</td>
                <td>{c.descricao}</td>
                <td className="text-end">
                  {dfd.tramitacao === 'EM ELABORAÇÃO' ? (
                    <select
                      name="categoria"
                      value={c.categoria}
                      onChange={e =>
                        handleInputChange(index, 'categoria', e.target.value)
                      }
                      className="form-select form-select-sm"
                      style={{ minWidth: '100px' }}
                    >
                      <option value="">Selecione...</option>
                      {categoriaItem.map(categoria => (
                        <option key={categoria.id} value={categoria.descricao}>
                          {categoria.descricao}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span>{c.categoria}</span>
                  )}
                </td>
                <td className="text-end">
                  {dfd.tramitacao === 'EM ELABORAÇÃO' ? (
                    <select
                      name="unidade"
                      value={c.unidade}
                      onChange={e =>
                        handleInputChange(index, 'unidade', e.target.value)
                      }
                      className="form-select form-select-sm"
                      style={{ minWidth: '100px' }}
                    >
                      <option value="">Selecione...</option>
                      {unidades.map(unidade => (
                        <option key={unidade.id} value={unidade.descricao}>
                          {unidade.descricao}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span>{c.unidade}</span>
                  )}
                </td>
                <td className="text-end">
                  {dfd.tramitacao === 'EM ELABORAÇÃO' ? (
                    <input
                      type="number"
                      name="quantidade"
                      value={c.quantidade ?? ''}
                      onChange={e =>
                        handleInputChange(index, 'quantidade', e.target.value)
                      }
                      className="form-control form-control-sm text-end"
                      style={{ minWidth: '100px' }}
                      min="0"
                    />
                  ) : (
                    <span>{formatQuantidade(c.quantidade)}</span>
                  )}
                </td>
                <td className="text-end">
                  {dfd.tramitacao === 'EM ELABORAÇÃO' ? (
                    <input
                      type="text"
                      name="vlrUnit"
                      value={c.vlrUnit ?? ''}
                      onChange={e =>
                        handleInputChange(index, 'vlrUnit', e.target.value)
                      }
                      className="form-control form-control-sm text-end"
                      style={{ minWidth: '100px' }}
                    />
                  ) : (
                    <span>{c.vlrUnit ?? ''}</span>
                  )}
                </td>
                <td className="text-end">
                  {dfd.tramitacao === 'EM ELABORAÇÃO' ? (
                    <input
                      disabled
                      type="text"
                      name="vlrTotal"
                      value={calculateTotal(c.quantidade, c.vlrUnit)}
                      className="form-control form-control-sm text-end"
                      style={{ minWidth: '100px' }}
                    />
                  ) : (
                    <span>{calculateTotal(c.quantidade, c.vlrUnit)}</span>
                  )}
                </td>
                {dfd.tramitacao === 'EM ELABORAÇÃO' && (
                  <td>
                    <button
                      onClick={() => handleDelete(c)}
                      className="btn btn-sm"
                      data-bs-toggle="tooltip"
                      data-bs-placement="top"
                      title="Excluir item"
                    >
                      <i className="bi bi-trash3 text-danger" />
                    </button>
                  </td>
                )}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="9" className="text-center">
                Nenhum registro encontrado.
              </td>
            </tr>
          )}
        </tbody>
      </TableContainer>
      <PageFooter>
        <Pagination10 {...setPagination} />
      </PageFooter>
      <ExcelUploadModal
        show={showExcelUploadModal}
        onClose={() => setShowExcelUploadModal(false)}
        onSuccess={() => setShouldFetch(!shouldFetch)}
        dfd={dfd}
      />
          <DfdFindItemModal
            isOpen={showFindModal}
            onClose={() => setShowFindModal(false)}
            dfd={{ ...dfd, tipo: dfd.tipo || location.state?.dfd?.tipo || '' }}
            onAdded={() => {
              setShowFindModal(false);
              setShouldFetch(prev => !prev);
            }}
          />
      <ConfirmModal {...setConfirmModal} />
      <DefaultModal {...setDefaultModal} />
        </PageContent>
      </ResponsiveShadow>
    </Layout>
  );
}

export default AprovaDfdItens;
